import React, { createContext, type PropsWithChildren, useCallback, useContext, useMemo, useRef } from 'react';
import { Color, Gradient } from '../types';
import { getColorType } from '../utils/getColorType';

type UserSelectedColors = {
    linear: Gradient | undefined;
    radial: Gradient | undefined;
    solid: Color | undefined;
};

interface ColorPickerContextValue {
    userColors: UserSelectedColors;
    setUserColor: (userColor: Color) => void;
    isColorDragging: boolean;
}

const ColorPickerContext = createContext<ColorPickerContextValue | undefined>(undefined);

type ColorPickerProviderProps = PropsWithChildren & { isColorDragging: boolean };

const initialState: UserSelectedColors = {
    linear: undefined,
    radial: undefined,
    solid: undefined,
};

export function ColorPickerProvider(props: ColorPickerProviderProps) {
    const colors = useRef({ ...initialState });

    const setUserColor = useCallback((color: Color) => {
        const type = getColorType(color);
        colors.current[type] = color as any;
    }, []);

    const value: ColorPickerContextValue = useMemo(
        () => ({
            userColors: colors.current,
            setUserColor,
            isColorDragging: props.isColorDragging,
        }),
        [setUserColor, props.isColorDragging],
    );

    return <ColorPickerContext.Provider value={value}>{props.children}</ColorPickerContext.Provider>;
}

export function useColorPicker() {
    const context = useContext(ColorPickerContext);

    if (context === undefined) {
        throw new Error('useColorPicker must be used within a ColorPickerProvider');
    }

    return context;
}
